/**
 * Action type definitions for the storytelling editor
 */

export const ACTION_TYPES = [
    { id: 'mediaaction',        icon: '🎬', label: 'Media' },
    { id: 'textaction',         icon: '💬', label: 'Text' },
    { id: 'sceneaction',        icon: '🎭', label: 'Szene' },
    { id: 'lightaction',        icon: '💡', label: 'Licht' },
    { id: 'menuaction',         icon: '📋', label: 'Menü' },
    { id: 'navigationaction',   icon: '🧭', label: 'Navigation' },
    { id: 'controlaction',      icon: '⚙️', label: 'Steuerung' },
    { id: 'stateaction',        icon: '📊', label: 'Status' },
    { id: 'contentaction',      icon: '📄', label: 'Inhalt' },
    { id: 'notificationaction', icon: '🔔', label: 'Benachrichtigung' },
    { id: 'interaction',        icon: '❓', label: 'Interaktion' },
    { id: 'conditionalaction',  icon: '◇',  label: 'Bedingung' },
    { id: 'caseaction',         icon: '◈',  label: 'Fallauswahl' },
];

export const ACTION_TYPE_MAP = Object.fromEntries(ACTION_TYPES.map(t => [t.id, t]));

export function getActionIcon(type) {
    return (ACTION_TYPE_MAP[type] && ACTION_TYPE_MAP[type].icon) || '▶';
}

export function getActionLabel(type) {
    return (ACTION_TYPE_MAP[type] && ACTION_TYPE_MAP[type].label) || type;
}

/**
 * Returns the human-readable title/name of an action, used as the PRIMARY label
 * in the timeline row. Falls back to type-specific field extraction.
 *
 * @param {object} action
 * @param {object} [referencedObjects]  - cache of loaded objects { [id]: object }
 */
export function getActionDescription(action, referencedObjects = {}) {
    if (!action || !action.fields) return '';
    const f = action.fields;
    const type = action.type;

    // Helper: resolve first ID in an array to a display name via the cache
    const resolveName = (ids) => {
        if (!Array.isArray(ids) || ids.length === 0) return null;
        const obj = referencedObjects[ids[0]];
        if (!obj) return null;
        return obj.title || (obj.fields && (obj.fields.name || obj.fields.title)) || null;
    };

    // Primary: explicit name or title on the action object itself
    if (f.name) return f.name;
    if (f.title) return f.title;

    // Type-specific fallbacks — derived from titlefields in type_*.json definitions
    switch (type) {
        case 'textaction':
        case 'notificationaction':
        case 'interaction': {
            // titlefields: ['text']  (or ['text', 'options.answer'] for interaction)
            if (f.text) {
                const raw = typeof f.text === 'string'
                    ? f.text
                    // Multilingual object — prefer German, then first non-empty value
                    : (typeof f.text === 'object'
                        ? (f.text.de || f.text.en || Object.values(f.text).find(v => v) || '')
                        : '');
                return raw.length > 60 ? raw.slice(0, 60) + '…' : raw;
            }
            // interaction fallback: first option's answer
            if (type === 'interaction' && Array.isArray(f.options) && f.options[0]) {
                const ans = f.options[0].fields && f.options[0].fields.answer;
                if (ans) return typeof ans === 'string' ? ans : (ans.de || ans.en || '');
            }
            break;
        }
        case 'controlaction':
        case 'navigationaction':
            // titlefields: ['command']
            return f.command || '';
        case 'lightaction': {
            // titlefields: ['command', 'commandvalue']
            const parts = [f.command, f.commandvalue].filter(Boolean);
            return parts.join(' ');
        }
        case 'menuaction': {
            // titlefields: ['menu.name', 'Slot', 'slot']
            const menuName = resolveName(f.menu);
            const slotPart = f.slot ? `Slot ${f.slot}` : '';
            if (menuName) return slotPart ? `${menuName} · ${slotPart}` : menuName;
            return slotPart;
        }
        case 'mediaaction': {
            // titlefields: ['medialist.title']
            const mediaName = resolveName(f.medialist);
            if (mediaName) {
                const extras = Array.isArray(f.medialist) ? f.medialist.length - 1 : 0;
                return extras > 0 ? `${mediaName} +${extras}` : mediaName;
            }
            const count = Array.isArray(f.medialist) ? f.medialist.length : 0;
            return count > 0 ? `${count} Medium/Medien` : '';
        }
        case 'sceneaction': {
            // titlefields: ['scene.name']
            const sceneName = resolveName(f.scene);
            if (sceneName) return `→ ${sceneName}`;
            const targets = f.scene || [];
            if (targets.length > 0) return `→ Szene (${targets.length})`;
            break;
        }
        case 'contentaction': {
            // titlefields: ['content.#title']
            const contentName = resolveName(f.content);
            if (contentName) return contentName;
            const count = Array.isArray(f.content) ? f.content.length : 0;
            return count > 0 ? `${count} Inhalt/Inhalte` : '';
        }
        case 'stateaction': {
            // titlefields: ['property.name', method-indicator, 'value']
            const propName = resolveName(f.property);
            const method = f.method === 'add' ? '+' : '←';
            if (propName && f.value !== undefined) return `${propName} ${method} ${f.value}`;
            if (propName) return propName;
            return f.value !== undefined ? `${method} ${f.value}` : '';
        }
        case 'conditionalaction': {
            // Try to show the first condition's value as a meaningful label
            const firstCond = Array.isArray(f.conditions) && f.conditions[0];
            if (firstCond && firstCond.fields) {
                const cf = firstCond.fields;
                if (cf.value !== undefined && cf.value !== '') return String(cf.value);
                if (cf.min !== undefined && cf.max !== undefined) return `${cf.min} – ${cf.max}`;
                if (cf.probability !== undefined) return `Zufall ${cf.probability}%`;
                if (cf.from || cf.to) return `Zeit ${cf.from || '?'} – ${cf.to || '?'}`;
            }
            // Fallback: count-based summary
            const condCount = Array.isArray(f.conditions) ? f.conditions.length : 0;
            const ifCount = Array.isArray(f.actions) ? f.actions.length : 0;
            const elseCount = Array.isArray(f.alternative) ? f.alternative.length : 0;
            const parts = [];
            if (condCount > 0) parts.push(`${condCount} Bed.`);
            if (ifCount > 0) parts.push(`wenn: ${ifCount} Akt.`);
            if (elseCount > 0) parts.push(`sonst: ${elseCount} Akt.`);
            return parts.join(' · ');
        }
        case 'caseaction': {
            const caseCount = Array.isArray(f.cases) ? f.cases.length : 0;
            return caseCount > 0 ? `${caseCount} Fälle` : '';
        }
        default:
            break;
    }
    return '';
}

/**
 * Returns a short subtitle with structural info for complex action types
 * (shown below the name in the action row).
 */
export function getActionSubtitle(action) {
    if (!action || !action.fields) return '';
    const f = action.fields;
    const type = action.type;
    switch (type) {
        case 'conditionalaction': {
            const condCount = Array.isArray(f.conditions) ? f.conditions.length : 0;
            const ifCount = Array.isArray(f.actions) ? f.actions.length : 0;
            const elseCount = Array.isArray(f.alternative) ? f.alternative.length : 0;
            const parts = [];
            if (condCount > 1) parts.push(`${condCount} Bed.`);
            if (ifCount > 0) parts.push(`wenn: ${ifCount}`);
            if (elseCount > 0) parts.push(`sonst: ${elseCount}`);
            return parts.join(' · ');
        }
        case 'caseaction': {
            const caseCount = Array.isArray(f.cases) ? f.cases.length : 0;
            return caseCount > 0 ? `${caseCount} Fälle` : '';
        }
        default:
            return '';
    }
}

export function getStartLabel(start) {
    switch (start) {
        case 'initially':     return 'Start';
        case 'withprevious':  return '‖ parallel';
        case 'afterprevious': return '→ danach';
        default:              return 'Start';
    }
}

export function getStartColor(start) {
    switch (start) {
        case 'withprevious':  return '#e8f5e9';
        case 'afterprevious': return '#fff8e1';
        default:              return '#e3f2fd';
    }
}

export function getStartBorderColor(start) {
    switch (start) {
        case 'withprevious':  return '#4caf50';
        case 'afterprevious': return '#ff9800';
        default:              return '#1976d2';
    }
}
